// src/components/Admin/AdminSettings.js - System Settings Control Room
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Save,
  Refresh,
  Settings as SettingsIcon,
  Person,
  AutoAwesome,
} from '@mui/icons-material';
import api from '../../services/api';

function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [settings, setSettings] = useState({
    autoAssignmentEnabled: true,
    defaultMaxCapacity: 10,
    systemName: 'Dycovue'
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/settings/global');
      if (response.data.success) {
        setSettings(response.data.settings);
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const response = await api.patch('/admin/settings/global', settings);
      
      if (response.data.success) {
        setSuccess('Settings saved successfully');
        setSettings(response.data.settings);
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.error || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkCapacityUpdate = async () => {
    if (!window.confirm(`Update all radiologists' capacity to ${settings.defaultMaxCapacity}? This will only affect radiologists whose current workload is below this limit.`)) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const response = await api.patch('/admin/radiologists/capacity/bulk', {
        maxCapacity: settings.defaultMaxCapacity
      });
      
      if (response.data.success) {
        setSuccess(`Updated capacity for ${response.data.updated.length} radiologist(s). ${response.data.skipped ? `${response.data.skipped.length} skipped due to current workload.` : ''}`);
      }
    } catch (err) {
      console.error('Error updating bulk capacity:', err);
      setError(err.response?.data?.error || 'Failed to update radiologist capacity. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          System Settings
        </Typography>
        <Box>
          <Tooltip title="Refresh settings">
            <span>
              <IconButton onClick={loadSettings} disabled={loading || saving} sx={{ mr: 1 }}>
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving}
          >
            Save Settings
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Automation Engine Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <AutoAwesome sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h5" component="h2">
                  Automation Engine
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                p={2}
                sx={{
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Auto-Assignment (Round-Robin)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Automatically route new report requests to the least busy specialist.
                    When disabled, reports will remain in "Pending" status for manual assignment.
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.autoAssignmentEnabled}
                      onChange={handleChange('autoAssignmentEnabled')}
                      color="primary"
                    />
                  }
                  label={settings.autoAssignmentEnabled ? 'Enabled' : 'Disabled'}
                  labelPlacement="top"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Specialist Capacity Management Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Person sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h5" component="h2">
                  Specialist Capacity Management
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Box p={2} sx={{ bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Default Max Reports per Specialist
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  This sets the default maximum number of concurrent reports each radiologist can handle.
                  Individual limits can be overridden in the Radiologist Management tab.
                </Typography>
                
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <TextField
                    type="number"
                    label="Default Capacity"
                    value={settings.defaultMaxCapacity}
                    onChange={handleChange('defaultMaxCapacity')}
                    inputProps={{ min: 1, max: 100 }}
                    sx={{ minWidth: 200 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="body2" color="text.secondary">
                            reports
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleBulkCapacityUpdate}
                    disabled={saving}
                  >
                    Update All Radiologists
                  </Button>
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Note: Bulk update will only affect radiologists whose current workload is below the new capacity limit.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* System Information Card */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SettingsIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h5" component="h2">
                  System Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <TextField
                fullWidth
                label="System Name"
                value={settings.systemName}
                onChange={handleChange('systemName')}
                sx={{ mb: 2 }}
                helperText="Display name for the system"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AdminSettings;

